import { Switch } from "@base-ui/react/switch";
import { twMerge } from "tailwind-merge";

/**
 * An on/off switch, per the design system's Toggle.
 * @see https://www.figma.com/design/gufDjkozUfXf6IYe5dhndc/Design-System?node-id=27-1272
 */

interface ToggleProps {
    checked?: boolean;
    /** Submitted with a surrounding form. */
    name?: string;
    disabled?: boolean;
    onChange?: (newChecked: boolean) => void;
    size?: "default" | "small";
    className?: string;
    /** The switch's accessible name, when nothing beside it labels it. */
    ariaLabel?: string;
}

const TRACK_CLASS = twMerge(
    "relative inline-flex shrink-0 cursor-pointer items-center rounded-full",
    // Bronze when on, stone when off.
    "bg-stone-300 data-checked:bg-bronze-600",
    "transition-colors duration-200 ease-in-out",
    // Keyboard focus: a bronze glow, the same as Checkbox.
    "outline-none focus-visible:shadow-focus",
    "data-disabled:cursor-not-allowed data-disabled:opacity-50",
);

const THUMB_CLASS = twMerge(
    "pointer-events-none block rounded-full bg-white shadow",
    "transition-transform duration-200 ease-in-out",
);

// Track and thumb sizes, and how far the thumb sits from each end.
const SIZE_CLASSES = {
    default: { track: "h-6 w-10", thumb: "size-4 translate-x-1 data-checked:translate-x-5" },
    small: { track: "h-4.5 w-7.5", thumb: "size-3 translate-x-0.75 data-checked:translate-x-3.75" },
} as const;

export default function Toggle({ checked = false, name, disabled = false, onChange, size = "default", className, ariaLabel }: ToggleProps) {
    const sizeClasses = SIZE_CLASSES[size];

    return (
        <div className="flex flex-row-reverse items-center">
            <Switch.Root
                checked={checked}
                onCheckedChange={newChecked => onChange?.(newChecked)}
                disabled={disabled}
                name={name}
                aria-label={ariaLabel}
                className={twMerge(TRACK_CLASS, sizeClasses.track, className)}
            >
                <Switch.Thumb className={twMerge(THUMB_CLASS, sizeClasses.thumb)} />
            </Switch.Root>
        </div>
    );
}
